import { FormikProps } from "formik"
import React, { forwardRef, Suspense, useRef } from "react"

import { useNavigate } from "react-router"
import { useParams } from "react-router-dom"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { PageHeader } from "../../../../../../shared/components/pageHeader/PageHeader"
import { Page } from "../../../../../../shared/components/pageHeader/PageHeaderApi"
import { StandardButton } from "../../../../../../shared/components/buttons/StandardButton"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../../shared/components/links/StandardBreadcrumbs"
import { QueryErrorBoundary } from "../../../../../../core/api/components/QueryErrorBoundary"
import { CreateUpdateProductGroupNumberForm } from "../components/CreateUpdateProductGroupNumberForm"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { SuccessDisplay } from "../../../../../../core/api/errorTypes"
import { queryKeyFor, useApiQuery } from "../../../../../../core/api/useApiQuery"
import { LoadingSpinner } from "../../../../../../shared/components/loading/LoadingSpinner"
import { CreateOrEditProductNumber } from "../productNumbersTypes"
import { SUPPLIER_ADMIN_PRODUCT_NUMBERS_ROUTE, SUPPLIER_ADMIN_ROUTE } from "../../../../../../constants/routes"
import {
    editProductApi,
    FetchDetailedProductNumberResponse,
    fetchDetailedProductNumbers,
    ProductNumbersQuery,
} from "../productNumbersApi"

function transformData({ productGroupNumber }: FetchDetailedProductNumberResponse) {
    return {
        name: productGroupNumber.name,
        isActive: productGroupNumber.isActive,
        code: productGroupNumber.code,
        description: productGroupNumber.description,
        history: productGroupNumber.history,
    }
}

export function EditProductNumber() {
    const formRef = useRef<FormikProps<CreateOrEditProductNumber>>(null)

    const handleSaveClick = () => {
        formRef.current?.submitForm()
    }

    return (
        <>
            <LocalizedStrict id="supplier-admin-erp-product-group-numbers-update-header" attrs={{ title: true }}>
                <PageHeader
                    page={Page.SUPPLIER_PRODUCTS_GROUPS_NUMBERS_UPDATE}
                    title="Edit product group number"
                    rightElement={
                        <LocalizedStrict id="supplier-admin-erp-product-group-numbers-save-button">
                            <StandardButton onClick={handleSaveClick}>Save</StandardButton>
                        </LocalizedStrict>
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="supplier-nav-item-home">
                            <BreadcrumbLink to={SUPPLIER_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="supplier-admin-nav-item-erp">
                            <Breadcrumb>ERP</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="supplier-admin-nav-item-product-numbers">
                            <BreadcrumbLink to={SUPPLIER_ADMIN_PRODUCT_NUMBERS_ROUTE}>Product numbers</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="supplier-admin-nav-item-update-product-group-number">
                            <Breadcrumb>Edit product number</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>

            <QueryErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                    <ProductGroupNumber ref={formRef} />
                </Suspense>
            </QueryErrorBoundary>
        </>
    )
}

const ProductGroupNumber = forwardRef<FormikProps<CreateOrEditProductNumber>>((props, ref) => {
    const navigate = useNavigate()
    const { productGroupsNumbersId } = useParams()
    const productGroupsNumbersIdNumber = Number(productGroupsNumbersId)

    const detailedProductGroupNumber = useApiQuery(fetchDetailedProductNumbers, {
        queryName: ProductNumbersQuery.DETAILS,
        dependencies: {
            id: productGroupsNumbersIdNumber ?? 0,
        },
        enabled: !!productGroupsNumbersIdNumber,
        suspense: true,
        useErrorBoundary: true,
        select: transformData,
    })

    const { mutate: callUpdateProductGroupNumber } = useApiMutation(editProductApi, {
        successDisplay: SuccessDisplay.SNACKBAR,
        async onSuccess(_, __, context) {
            await context.queryClient.invalidateQueries({ queryKey: [ProductNumbersQuery.LIST] })
            await context.queryClient.invalidateQueries({
                queryKey: queryKeyFor(ProductNumbersQuery.DETAILS, { id: productGroupsNumbersId }),
            })
            navigate(SUPPLIER_ADMIN_PRODUCT_NUMBERS_ROUTE)
        },
    })

    const editProductGroupNumber = (data: CreateOrEditProductNumber) => {
        const productGroupNumber = {
            code: data.code,
            name: data.name,
            isActive: data.isActive,
            description: data.description,
        }

        callUpdateProductGroupNumber({ id: productGroupsNumbersIdNumber, productGroupNumber })
    }

    return (
        <CreateUpdateProductGroupNumberForm
            ref={ref}
            initialValues={detailedProductGroupNumber.data}
            history={detailedProductGroupNumber.data?.history}
            onSubmit={editProductGroupNumber}
        />
    )
})
